import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class EmailValidator {
  /**
   * Strict email pattern that requires:
   * - Valid local part (before @)
   * - @ symbol
   * - Domain with at least one dot
   * - TLD with at least 2 characters
   */
  static readonly STRICT_EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /**
   * Validator function for strict email format
   * Requires proper domain with dot and TLD (e.g., .com, .org, .net)
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static strictEmail(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const email = control.value.toString().trim().toLowerCase();
    
    // Check if it matches strict email pattern
    if (!EmailValidator.STRICT_EMAIL_PATTERN.test(email)) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Please enter a valid email address '
        } 
      };
    }

    // Additional validation for domain structure
    const parts = email.split('@');
    if (parts.length !== 2) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Email must contain exactly one @ symbol'
        } 
      };
    }

    const [localPart, domain] = parts;

    // Validate local part (before @)
    if (localPart.length === 0) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Email must have a username before @ symbol'
        } 
      };
    }

    // Validate domain structure
    const domainParts = domain.split('.');
    if (domainParts.length < 2) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Email domain must contain at least one dot (e.g., example.com)'
        } 
      };
    }

    // Validate TLD (last part after final dot)
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Email domain extension must be at least 2 characters long'
        } 
      };
    }

    // Validate domain name (before TLD)
    const domainName = domainParts[domainParts.length - 2];
    if (!domainName || domainName.length === 0) {
      return { 
        strictEmail: { 
          value: control.value,
          message: 'Email must have a valid domain name before the extension'
        } 
      };
    }

    return null; // Valid email
  }

  /**
   * Check if email is valid with strict format
   * @param email - Email to validate
   * @returns boolean
   */
  static isValidStrictEmail(email: string): boolean {
    if (!email) return false;
    
    const control = { value: email } as AbstractControl;
    return EmailValidator.strictEmail(control) === null;
  }

  /**
   * Format email to lowercase (standard practice)
   * @param email - Raw email string
   * @returns Formatted email or original if invalid
   */
  static formatEmail(email: string): string {
    if (!email) return email;
    return email.trim().toLowerCase();
  }
}

/**
 * Factory function to create strict email validator
 * @returns ValidatorFn
 */
export function strictEmailValidator(): ValidatorFn {
  return EmailValidator.strictEmail;
}
